import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Collapse,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  LinearProgress,
  MenuItem,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { visuallyHidden } from "@mui/utils";
import { DatePicker } from "antd";
import dayjs from "dayjs";
import HistoryIcon from "@mui/icons-material/History";
import HistoryToggleOffIcon from "@mui/icons-material/HistoryToggleOff";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import SearchIcon from "@mui/icons-material/Search";
import SearchOffIcon from "@mui/icons-material/SearchOff";
import { GetMasterChangeLog, GetUser } from "../../../Service/_index";
import { debounce } from "../../../Shared/debounce";
import MasterHistoryDialog from "../../../UiComponents/MasterHistory/MasterHistoryDialog";
import { useMasterHistoryAccess } from "../../../UiComponents/MasterHistory/useMasterHistoryAccess";
import {
  ActionChip,
  ChangeList,
  HistoryEmpty,
  HistoryError,
  Person,
  VersionPill,
  outlinedButtonSx,
} from "../../../UiComponents/MasterHistory/HistoryParts";
import {
  ACTIONS,
  ACTION_SENTENCE,
  changeSummary,
  formatDateTime,
  timeAgo,
} from "../../../UiComponents/MasterHistory/historyFormat";
import Styles from "./MasterChangeLog.module.scss";

const { RangePicker } = DatePicker;

const EMPTY_FILTERS = { entityType: "", action: "", changedBy: "", range: null, search: "" };
const ACTION_OPTIONS = ["created", "updated", "enabled", "disabled", "deleted"];
const COLUMNS = ["When", "Master", "Record", "Version", "Action", "Changed by", "Changes"];
const COLUMN_COUNT = COLUMNS.length + 1;

// index.scss forces every outlined input border transparent; restated so the boxes read as boxes.
const inputSx = {
  backgroundColor: "#ffffff",
  "& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline": {
    borderColor: "#D0D5DD !important",
  },
  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "#1E3A5F !important",
    borderWidth: "2px !important",
  },
};

const buildPresets = () => {
  const today = dayjs().endOf("day");
  return [
    { label: "Today", value: [dayjs().startOf("day"), today] },
    { label: "Last 7 days", value: [dayjs().subtract(6, "day").startOf("day"), today] },
    { label: "Last 30 days", value: [dayjs().subtract(29, "day").startOf("day"), today] },
    { label: "This month", value: [dayjs().startOf("month"), today] },
    {
      label: "Last month",
      value: [
        dayjs().subtract(1, "month").startOf("month"),
        dayjs().subtract(1, "month").endOf("month"),
      ],
    },
  ];
};

const FieldLabel = ({ children }) => (
  <Typography sx={{ mb: 0.5, fontSize: "11.5px", fontWeight: 500, color: "#667085" }}>
    {children}
  </Typography>
);

const StatePanel = (props) => (
  <Box sx={{ py: 3 }}>
    <HistoryEmpty {...props} />
  </Box>
);

const SkeletonRows = () =>
  Array.from({ length: 6 }).map((_, index) => (
    <TableRow key={index}>
      {Array.from({ length: COLUMN_COUNT }).map((__, cell) => (
        <TableCell key={cell} className={Styles.bodyCell}>
          <Skeleton variant="text" width={cell === 0 ? 20 : "80%"} />
        </TableCell>
      ))}
    </TableRow>
  ));

const MasterChangeLog = () => {
  const access = useMasterHistoryAccess();

  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [searchInput, setSearchInput] = useState("");
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(25);
  const [refreshKey, setRefreshKey] = useState(0);

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [error, setError] = useState("");
  const [turnedOff, setTurnedOff] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [historyTarget, setHistoryTarget] = useState(null);
  const [users, setUsers] = useState([]);

  const requestId = useRef(0);
  const latestSearch = useRef("");
  const presets = useMemo(buildPresets, []);

  const canQuery = access.allowed && access.enabled && !turnedOff;

  // Debounced typing; the ref drops a pending commit that a Reset has made stale.
  const commitSearch = useMemo(
    () =>
      debounce((value) => {
        if (value !== latestSearch.current) return;
        const search = value.trim();
        setFilters((prev) => (prev.search === search ? prev : { ...prev, search }));
        setPage(0);
      }, 400),
    []
  );

  const params = useMemo(() => {
    const query = { page, limit };
    if (filters.entityType) query.entityType = filters.entityType;
    if (filters.action) query.action = filters.action;
    if (filters.changedBy) query.changedBy = filters.changedBy;
    if (filters.search) query.search = filters.search;
    if (filters.range) {
      query.from = filters.range[0].startOf("day").toISOString();
      query.to = filters.range[1].endOf("day").toISOString();
    }
    return query;
  }, [filters, page, limit]);

  useEffect(() => {
    if (!canQuery) return;
    const id = ++requestId.current;
    setLoading(true);
    setError("");
    GetMasterChangeLog(params).then((res) => {
      if (id !== requestId.current) return;
      setLoading(false);
      if (res.error) {
        setError(res.message || "Could not load the change log");
        return;
      }
      if (res.enabled === false) {
        setTurnedOff(true);
        return;
      }
      setRows(res.data || []);
      setTotal(res.total || 0);
      setExpandedId(null);
      setHasLoaded(true);
    });
  }, [canQuery, params, refreshKey]);

  useEffect(() => {
    if (!access.allowed || !access.enabled) return;
    GetUser({ isAscending: true, requesttype: "TABLE" }).then((res) => setUsers(res?.data || []));
  }, [access.allowed, access.enabled]);

  const changedByOptions = useMemo(() => {
    const byId = new Map(users.map((user) => [String(user._id), user.name || user.email]));
    const myId = sessionStorage.getItem("UserId");
    if (myId) byId.set(myId, `${sessionStorage.getItem("name") || "You"} (you)`);
    return [...byId.entries()]
      .filter(([, label]) => label)
      .map(([id, label]) => ({ id, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [users]);

  const masterOptions = useMemo(
    () => [...access.entities].sort((a, b) => a.label.localeCompare(b.label)),
    [access.entities]
  );

  const updateFilters = (patch) => {
    setFilters((prev) => ({ ...prev, ...patch }));
    setPage(0);
  };

  const clearSearch = () => {
    latestSearch.current = "";
    setSearchInput("");
    updateFilters({ search: "" });
  };

  const resetAll = () => {
    latestSearch.current = "";
    setSearchInput("");
    setFilters(EMPTY_FILTERS);
    setPage(0);
  };

  const activeChips = [
    filters.entityType && {
      key: "entityType",
      label: `Master: ${masterOptions.find((m) => m.entityType === filters.entityType)?.label || filters.entityType}`,
      clear: () => updateFilters({ entityType: "" }),
    },
    filters.action && {
      key: "action",
      label: `Action: ${ACTIONS[filters.action].label}`,
      clear: () => updateFilters({ action: "" }),
    },
    filters.changedBy && {
      key: "changedBy",
      label: `Changed by: ${changedByOptions.find((u) => u.id === filters.changedBy)?.label || "selected user"}`,
      clear: () => updateFilters({ changedBy: "" }),
    },
    filters.range && {
      key: "range",
      label: `${filters.range[0].format("DD MMM YYYY")} – ${filters.range[1].format("DD MMM YYYY")}`,
      clear: () => updateFilters({ range: null }),
    },
    filters.search && { key: "search", label: `Search: "${filters.search}"`, clear: clearSearch },
  ].filter(Boolean);

  const showSkeleton = !hasLoaded || (loading && rows.length === 0);

  const toggleRow = (id) => setExpandedId((current) => (current === id ? null : id));

  const openHistory = (row) =>
    setHistoryTarget({
      open: true,
      entityType: row.entityType,
      entityId: row.entityId,
      recordLabel: row.recordLabel,
      focusVersion: row.version,
    });

  const renderBody = () => {
    if (!access.allowed) {
      return (
        <StatePanel
          icon={<LockOutlinedIcon />}
          title="This page is restricted"
          text="The master change log is available to administrators and accountants only."
        />
      );
    }
    if (!access.ready) {
      return (
        <Box sx={{ p: 2.5 }}>
          <Skeleton variant="rounded" height={140} sx={{ borderRadius: "8px" }} />
        </Box>
      );
    }
    if (access.error) {
      return (
        <Box sx={{ p: 2.5 }}>
          <HistoryError message={access.error} onRetry={access.retry} />
        </Box>
      );
    }
    if (!access.enabled || turnedOff) {
      return (
        <StatePanel
          icon={<HistoryToggleOffIcon />}
          title="History tracking is turned off"
          text="Changes to master data are not being recorded right now, so there is nothing to show. Ask your system administrator to turn on master history."
        />
      );
    }

    return (
      <>
        <Box className="Master_Card_Sub_Container">
          <Box
            sx={{
              display: "grid",
              gap: 1.5,
              gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(12, 1fr)" },
              "& > div": { minWidth: 0 },
            }}
          >
            <Box sx={{ gridColumn: { md: "span 3" } }}>
              <FieldLabel>Master</FieldLabel>
              <Autocomplete
                size="small"
                options={masterOptions}
                value={masterOptions.find((m) => m.entityType === filters.entityType) || null}
                getOptionLabel={(option) => option.label}
                isOptionEqualToValue={(option, value) => option.entityType === value.entityType}
                onChange={(_, option) => updateFilters({ entityType: option?.entityType || "" })}
                renderInput={(inputParams) => (
                  <TextField {...inputParams} placeholder="All masters" sx={inputSx} />
                )}
              />
            </Box>

            <Box sx={{ gridColumn: { md: "span 2" } }}>
              <FieldLabel>Action</FieldLabel>
              <TextField
                select
                fullWidth
                size="small"
                value={filters.action}
                onChange={(event) => updateFilters({ action: event.target.value })}
                SelectProps={{ displayEmpty: true }}
                sx={inputSx}
              >
                <MenuItem value="">All actions</MenuItem>
                {ACTION_OPTIONS.map((action) => (
                  <MenuItem key={action} value={action}>
                    {ACTIONS[action].label}
                  </MenuItem>
                ))}
              </TextField>
            </Box>

            <Box sx={{ gridColumn: { md: "span 3" } }}>
              <FieldLabel>Changed by</FieldLabel>
              <Autocomplete
                size="small"
                options={changedByOptions}
                value={changedByOptions.find((u) => u.id === filters.changedBy) || null}
                getOptionLabel={(option) => option.label}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                onChange={(_, option) => updateFilters({ changedBy: option?.id || "" })}
                renderInput={(inputParams) => (
                  <TextField {...inputParams} placeholder="Anyone" sx={inputSx} />
                )}
              />
            </Box>

            <Box sx={{ gridColumn: { md: "span 4" } }}>
              <FieldLabel>Date range</FieldLabel>
              <RangePicker
                value={filters.range}
                presets={presets}
                format="DD MMM YYYY"
                allowClear
                disabledDate={(date) => date && date.isAfter(dayjs().endOf("day"))}
                onChange={(dates) => updateFilters({ range: dates })}
                style={{ width: "100%", height: 40, borderColor: "#D0D5DD", borderRadius: 4 }}
              />
            </Box>

            <Box sx={{ gridColumn: { md: "span 4" } }}>
              <TextField
                fullWidth
                size="small"
                value={searchInput}
                placeholder="Search record or person"
                inputProps={{ "aria-label": "Search by record or person" }}
                onChange={(event) => {
                  latestSearch.current = event.target.value;
                  setSearchInput(event.target.value);
                  commitSearch(event.target.value);
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ fontSize: 20, color: "#667085" }} />
                    </InputAdornment>
                  ),
                }}
                sx={inputSx}
              />
            </Box>

            {activeChips.length > 0 && (
              <Box
                sx={{
                  gridColumn: { md: "span 8" },
                  display: "flex",
                  flexWrap: "wrap",
                  alignItems: "center",
                  gap: 0.75,
                }}
              >
                {activeChips.map((chip) => (
                  <Chip
                    key={chip.key}
                    label={chip.label}
                    size="small"
                    onDelete={chip.clear}
                    sx={{
                      height: 24,
                      fontSize: "12px",
                      color: "#1E3A5F",
                      backgroundColor: "#EEF3F9",
                      "& .MuiChip-deleteIcon": { color: "#667085", fontSize: 16 },
                    }}
                  />
                ))}
              </Box>
            )}
          </Box>
        </Box>

        {error && (
          <Box sx={{ px: 2.25, pb: 2 }}>
            <HistoryError message={error} onRetry={() => setRefreshKey((n) => n + 1)} />
          </Box>
        )}

        {!error && hasLoaded && !loading && rows.length === 0 ? (
          <StatePanel
            icon={<SearchOffIcon />}
            title={activeChips.length ? "No changes match these filters" : "No changes recorded yet"}
            text={
              activeChips.length
                ? "Try widening the date range or clearing a filter."
                : "Edits to master data will appear here as they happen."
            }
            action={
              activeChips.length ? (
                <Button size="small" variant="outlined" onClick={resetAll} sx={{ ...outlinedButtonSx, mt: 1 }}>
                  Clear filters
                </Button>
              ) : null
            }
          />
        ) : (
          !error && (
            <Box className={Styles.tableWrap}>
              <Box sx={{ height: 3 }}>{loading && !showSkeleton && <LinearProgress sx={{ height: 3 }} />}</Box>
              <Box className={Styles.tableScroll}>
                <Table aria-label="Master change log" sx={{ minWidth: 980 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell className={Styles.headCell} sx={{ width: 48 }}>
                        <Box component="span" sx={visuallyHidden}>
                          Details
                        </Box>
                      </TableCell>
                      {COLUMNS.map((label) => (
                        <TableCell key={label} className={Styles.headCell}>
                          {label}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody className={loading && !showSkeleton ? Styles.dim : ""}>
                    {showSkeleton ? (
                      <SkeletonRows />
                    ) : (
                      rows.map((row) => {
                        const isOpen = expandedId === row._id;
                        return (
                          <React.Fragment key={row._id}>
                            <TableRow
                              className={`${Styles.row} ${isOpen ? Styles.rowOpen : ""}`}
                              onClick={() => toggleRow(row._id)}
                            >
                              <TableCell className={Styles.bodyCell} sx={{ width: 48, px: "6px !important" }}>
                                <IconButton
                                  size="small"
                                  aria-label={isOpen ? "Hide details" : "Show details"}
                                  aria-expanded={isOpen}
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    toggleRow(row._id);
                                  }}
                                >
                                  <KeyboardArrowDownIcon
                                    fontSize="small"
                                    sx={{
                                      color: "#1E3A5F",
                                      transition: "transform 0.2s",
                                      transform: isOpen ? "rotate(180deg)" : "none",
                                    }}
                                  />
                                </IconButton>
                              </TableCell>
                              <TableCell className={Styles.bodyCell} sx={{ whiteSpace: "nowrap" }}>
                                <Box sx={{ fontWeight: 500 }}>{formatDateTime(row.changedAt)}</Box>
                                <Box sx={{ fontSize: "11.5px", color: "#667085" }}>{timeAgo(row.changedAt)}</Box>
                              </TableCell>
                              <TableCell className={Styles.bodyCell} sx={{ fontWeight: 500 }}>
                                {row.entityLabel}
                              </TableCell>
                              <TableCell className={Styles.bodyCell} sx={{ maxWidth: 260 }}>
                                <Tooltip title={row.recordLabel || ""}>
                                  <Box sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                    {row.recordLabel || "—"}
                                  </Box>
                                </Tooltip>
                              </TableCell>
                              <TableCell className={Styles.bodyCell}>
                                <VersionPill version={row.version} />
                              </TableCell>
                              <TableCell className={Styles.bodyCell}>
                                <ActionChip action={row.action} />
                              </TableCell>
                              <TableCell className={Styles.bodyCell}>
                                <Person name={row.changedByName} />
                              </TableCell>
                              <TableCell className={Styles.bodyCell} sx={{ color: "#475467 !important", whiteSpace: "nowrap" }}>
                                {changeSummary(row)}
                              </TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell className={Styles.detailCell} colSpan={COLUMN_COUNT}>
                                <Collapse in={isOpen} unmountOnExit>
                                  <Box className={Styles.detail}>
                                    {row.changes?.length ? (
                                      <ChangeList changes={row.changes} />
                                    ) : (
                                      <Typography sx={{ fontSize: "12.5px", color: "#475467" }}>
                                        {ACTION_SENTENCE[row.action] || "No field changes were recorded."}
                                      </Typography>
                                    )}
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      startIcon={<HistoryIcon sx={{ fontSize: "16px !important" }} />}
                                      onClick={() => openHistory(row)}
                                      sx={{ ...outlinedButtonSx, mt: 1.75 }}
                                    >
                                      View record history
                                    </Button>
                                  </Box>
                                </Collapse>
                              </TableCell>
                            </TableRow>
                          </React.Fragment>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </Box>
              <Box className={Styles.pagination}>
                <TablePagination
                  component="div"
                  rowsPerPageOptions={[10, 25, 50, 100]}
                  count={total}
                  rowsPerPage={limit}
                  page={page}
                  onPageChange={(_, next) => setPage(next)}
                  onRowsPerPageChange={(event) => {
                    setLimit(parseInt(event.target.value, 10));
                    setPage(0);
                  }}
                />
              </Box>
            </Box>
          )
        )}
      </>
    );
  };

  return (
    <>
      <Grid container className="Master_Card_Container">
        <Grid container alignItems="center" className="Master_Card_Sub_Container">
          <Grid item xs={12} sm={6} md={6}>
            <Typography className="Master_Card_Tittle">Master Change Log</Typography>
            <Typography sx={{ color: "#667085", fontSize: "12.5px" }}>
              Who changed which master record, and what changed
            </Typography>
          </Grid>
          <Grid
            item
            xs={12}
            sm={6}
            md={6}
            className="d-flex justify-content-end"
            sx={{ gap: 1, mt: { xs: 1.5, sm: 0 } }}
          >
            <Button
              className="Master_Card_Filter_Button"
              startIcon={<RestartAltIcon />}
              onClick={resetAll}
              disabled={!canQuery || !activeChips.length}
            >
              Reset
            </Button>
            <Button
              className="Master_Card_create_Button"
              startIcon={<RefreshIcon />}
              onClick={() => setRefreshKey((n) => n + 1)}
              disabled={!canQuery}
            >
              Refresh
            </Button>
          </Grid>
        </Grid>
        <Grid item xs={12}>
          <Divider sx={{ backgroundColor: "#EAECF0", borderWidth: 1 }} />
        </Grid>
        <Grid item xs={12}>
          {renderBody()}
        </Grid>
      </Grid>

      {historyTarget && (
        <MasterHistoryDialog
          open={historyTarget.open}
          onClose={() => setHistoryTarget((current) => ({ ...current, open: false }))}
          entityType={historyTarget.entityType}
          entityId={historyTarget.entityId}
          recordLabel={historyTarget.recordLabel}
          focusVersion={historyTarget.focusVersion}
        />
      )}
    </>
  );
};

export default MasterChangeLog;
